import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  House, FileText, UploadSimple, CheckSquareOffset,
  Clock, FolderSimple, Buildings, Users as UsersIcon, SignOut, ListBullets,
} from "@phosphor-icons/react";

const nav = [
  { to: "/", label: "Dashboard", icon: House, end: true, testid: "nav-dashboard" },
  { to: "/documents", label: "Documents", icon: FileText, testid: "nav-documents" },
  { to: "/toc", label: "Table of Contents", icon: ListBullets, testid: "nav-toc" },
  { to: "/upload", label: "Upload", icon: UploadSimple, testid: "nav-upload" },
  { to: "/approvals", label: "Approvals", icon: CheckSquareOffset, testid: "nav-approvals" },
  { to: "/expiring", label: "Expiring", icon: Clock, testid: "nav-expiring" },
  { to: "/categories", label: "Categories", icon: FolderSimple, testid: "nav-categories" },
  { to: "/departments", label: "Departments", icon: Buildings, testid: "nav-departments" },
  { to: "/users", label: "Users", icon: UsersIcon, testid: "nav-users", roles: ["admin", "manager"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-zinc-50">
      <aside className="w-64 shrink-0 bg-white border-r border-zinc-200 flex flex-col">
        <div className="px-5 py-5 border-b border-zinc-200">
          <img
            src="/radish-logo.webp"
            alt="Radish Technologies"
            className="h-10 w-auto object-contain"
            data-testid="sidebar-radish-logo"
          />
          <div className="mt-2.5">
            <div className="font-display text-base text-navy leading-none uppercase tracking-tight">Document Hub</div>
            <div className="label-caps mt-1">Manufacturing DMS</div>
          </div>
        </div>
        <nav className="flex-1 py-4">
          {nav.filter((item) => !item.roles || item.roles.includes(user?.role)).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}
            >
              <item.icon size={18} weight="regular" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-zinc-200 p-4">
          <div className="text-sm font-medium text-zinc-900 truncate" data-testid="current-user-name">
            {user?.name}
          </div>
          {(user?.designation || user?.department_name) && (
            <div className="text-[11px] text-slate-600 mt-0.5 truncate">
              {user?.designation}
              {user?.designation && user?.department_name && <span className="text-slate-400"> · </span>}
              {user?.department_name}
            </div>
          )}
          <div className="label-caps mt-0.5" data-testid="current-user-role">{user?.role}</div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-100 transition-colors"
          >
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <header className="bg-white border-b border-zinc-200 px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="/radish-logo.webp"
              alt="Radish Technologies"
              className="h-11 w-auto object-contain hidden sm:block"
              data-testid="header-radish-logo"
            />
            <div className="border-l border-zinc-200 pl-4 hidden sm:block">
              <div className="label-caps">Workspace</div>
              <h1 className="font-display text-2xl text-navy uppercase mt-1">Document Control</h1>
            </div>
            <div className="sm:hidden">
              <div className="label-caps">Workspace</div>
              <h1 className="font-display text-2xl text-navy uppercase mt-1">Document Control</h1>
            </div>
          </div>
          <div className="flex items-center gap-4" data-testid="header-user-card">
            <div className="text-right leading-tight">
              <div className="font-semibold text-navy text-sm" data-testid="header-user-name">
                {user?.name || "—"}
              </div>
              <div className="text-[11px] font-mono text-slate-500" data-testid="header-user-email">
                {user?.email || ""}
              </div>
              {(user?.designation || user?.department_name) && (
                <div className="text-[11px] text-slate-700 mt-0.5" data-testid="header-user-hr">
                  <span className="font-semibold" data-testid="header-user-designation">
                    {user?.designation || "—"}
                  </span>
                  <span className="text-slate-400"> · </span>
                  <span data-testid="header-user-department">{user?.department_name || "—"}</span>
                </div>
              )}
            </div>
            <div
              className="w-9 h-9 rounded-full bg-navy text-cyan flex items-center justify-center text-sm font-bold shrink-0"
              data-testid="header-user-avatar"
              title={user?.name || user?.email}
            >
              {(user?.name || user?.email || "?").trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("")}
            </div>
          </div>
        </header>
        <div className="p-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
